import { api } from '#client/lib/api';
import { type KeyedRequest, useKeyedRequest } from './useKeyedRequest';

/**
 * Fetches whatever lives at `path`. Pass `undefined` to skip.
 *
 * The general form; the two wrappers below cover every current caller and are
 * what feature code should reach for.
 */
export function useApiResource<T>(path: string | undefined): KeyedRequest<T> {
  return useKeyedRequest<T>(
    path,
    (signal) => api.get<T>(path ?? '', undefined, signal),
    'Could not load this record',
  );
}

/**
 * One record: `${basePath}/${id}`.
 *
 * Pass `id` as undefined to skip fetching — an "add" form reusing an "edit"
 * form's component, for instance.
 */
export function useResourceItem<T>(
  basePath: string,
  id: string | number | undefined,
): KeyedRequest<T> {
  return useApiResource<T>(id === undefined || id === '' ? undefined : `${basePath}/${id}`);
}

/**
 * A sub-collection: `${basePath}/${id}/${subPath}` — every report for a
 * patient, say.
 *
 * Use this rather than passing `${id}/reports` as the *id* to
 * `useResourceItem`, which only works by accident and requests `/patients/`
 * when the id is an empty string.
 */
export function useSubResource<T>(
  basePath: string,
  id: string | number | undefined,
  subPath: string,
): KeyedRequest<T> {
  return useApiResource<T>(
    id === undefined || id === '' ? undefined : `${basePath}/${id}/${subPath}`,
  );
}
